// -----------------------------------------------------------------------------
// Drives the @-mention picker in the chat composer.
//
// The picker is two-stage (like the omni search palette): the caller first
// chooses a type "folder", then this hook resolves matches for that type only.
// Users are matched instantly against the channel's already-loaded user map (no
// round-trip); vehicles / parts / orders / leads go through the shared omni
// search endpoint, scoped to the chosen type so the query stays cheap.
// -----------------------------------------------------------------------------

import { useMemo } from "react";

import useOmniSearch from "../data/useOmniSearch";
import { OmniSearchType } from "../models/search/OmniSearch";
import { IChatUser } from "../models/Chat";
import { fullNameFor } from "../utils/chatHelpers";
import { Mention, MentionType } from "../utils/chatMentions";

export interface MentionSuggestion extends Mention {
  /** Secondary line shown under the label in the picker (role, VRM, etc). */
  description?: string;
}

const MAX_USERS = 8;
const MAX_TOTAL = 10;

function mentionTypeToOmniType(type: MentionType): OmniSearchType | null {
  switch (type) {
    case "vehicle":
      return OmniSearchType.Vehicle;
    case "lead":
      return OmniSearchType.Lead;
    case "order":
      return OmniSearchType.Order;
    case "stock":
      return OmniSearchType.Stock;
    case "user":
    default:
      return null;
  }
}

export default function useMentionAutocomplete(
  query: string | null,
  selectedType: MentionType | null,
  usersById: Map<string, IChatUser>,
  meId: string
) {
  const isActive = query !== null && selectedType !== null;
  const trimmed = (query ?? "").trim();
  const isUserType = selectedType === "user";

  const userSuggestions = useMemo<MentionSuggestion[]>(() => {
    if (!isActive || !isUserType) return [];

    const lower = trimmed.toLowerCase();
    const matches: MentionSuggestion[] = [];

    for (const user of usersById.values()) {
      if (user.id === meId) continue;
      const name = fullNameFor(user);
      if (lower === "" || name.toLowerCase().includes(lower)) {
        matches.push({
          type: "user",
          id: user.id,
          label: name || "Unknown user",
          description: user.role,
        });
      }
      if (matches.length >= MAX_USERS) break;
    }

    return matches;
  }, [isActive, isUserType, trimmed, usersById, meId]);

  // Scoped entity search — the type is always sent now, so an empty query just
  // returns the most-recent rows of that one table (handy as a starting list).
  const omniType = selectedType ? mentionTypeToOmniType(selectedType) : null;
  const { omniSearch } = useOmniSearch(
    trimmed,
    omniType,
    isActive && !isUserType
  );

  const entitySuggestions = useMemo<MentionSuggestion[]>(() => {
    if (isUserType) return [];
    return (omniSearch.data?.data ?? [])
      .map((result): MentionSuggestion | null => {
        if (!selectedType) return null;
        return {
          type: selectedType,
          id: result.id,
          label: result.name,
          description: result.description ?? undefined,
        };
      })
      .filter((s): s is MentionSuggestion => s !== null)
      .slice(0, MAX_TOTAL);
  }, [isUserType, selectedType, omniSearch.data]);

  return {
    suggestions: isUserType ? userSuggestions : entitySuggestions,
    isLoading: isActive && !isUserType && omniSearch.isLoading,
  };
}
